"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Mail, MapPin, Clock, CircleCheck, Loader } from "lucide-react";
export default function ReachOurTeam() {
  return <section data-section-id="142759" id="schedule-your-free-pickup" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          
          {}
          <div className="lg:col-span-5 space-y-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 font-default">
                Give Your Clothes a Second Life
              </h2>
              <p className="text-muted-foreground text-lg font-sans">
                Schedule your free porch pickup today. We collect gently used items and donate them to local Ottawa charities, keeping quality textiles out of our landfills and in the community.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <MapPin className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg font-default">Service Area</h3>
                  <p className="text-muted-foreground mt-1 font-sans">Serving Kanata, Nepean, and Downtown Ottawa</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <Mail className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg font-default">Email Us</h3>
                  <p className="text-muted-foreground mt-1 font-sans">hello@secondchancethreads.ca</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <Clock className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg font-default">Pickup Schedule</h3>
                  <p className="text-muted-foreground mt-1 font-sans">Mon: Kanata | Tue: Nepean<br />Wed: Downtown Ottawa</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <CircleCheck className="size-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg font-default">No-Contact Service</h3>
                  <p className="text-muted-foreground mt-1 font-sans">Simply leave your bags on the porch by 8am on your scheduled day.</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-lg mb-4 font-default">What to Expect</h3>
              <ul className="space-y-3">
                {["No-contact porch collection", "Donated to local Ottawa charities", "Eco-friendly textile recycling", "Simple, reliable scheduling", "Local Ottawa service", "Free pickup. No hidden fees"].map((item, i) => <li key={i} data-index={i} className="flex items-center space-x-3 text-muted-foreground font-sans">
                    <CircleCheck className="size-5 text-secondary shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-lg backdrop-blur-md mb-8">
              <div className="mb-8">
                <h3 className="text-2xl font-bold font-default">Book Your Pickup</h3>
                <p className="text-muted-foreground mt-2 font-sans">Fill out the form below and we'll confirm your collection slot within 24 hours.</p>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 mb-8">
                {["Takes less than 30 seconds", "No contact needed", "Just leave your bags outside"].map((text, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-sm text-muted-foreground font-sans">
                    <CircleCheck className="size-4 text-secondary shrink-0" />
                    <span>{text}</span>
                  </div>)}
              </div>

              <FormProvider formKey="schedule_pickup_form_book_your_pickup" formId="schedule_pickup_form_book_your_pickup" sectionName="Schedule Your Free Pickup" defaultValues={{
              name: "",
              email: "",
              service_type: "",
              address: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              service_type: {
                required: true
              }
            }} submitText="Book My Pickup" successMessage="Thank you! Your pickup request has been received. We will send a confirmation email shortly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-12 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h4 className="text-xl font-bold text-foreground font-default">Pickup Scheduled Successfully</h4>
        <p className="text-muted-foreground max-w-md font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 font-sans">
          Schedule Another Pickup
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Full Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="font-sans" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^\S+@\S+\.\S+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Email Address <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="font-sans" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_type" rules={{
      required: "Please select your pickup day"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans">Pickup day <span className="text-destructive">*</span></FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="font-sans w-full">
                  <SelectValue placeholder="Select your pickup day" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {["Kanata (Mondays)", "Nepean (Tuesdays)", "Downtown Ottawa (Wednesdays)", "Other Ottawa Area"].map((option, i) => <SelectItem key={option} value={option} data-index={i} className="font-sans">
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans">Pickup Address</FormLabel>
            <FormControl>
              <Input placeholder="123 Maple Ave, Ottawa" className="font-sans" {...field} />
            </FormControl>
            <p className="text-[0.8rem] text-muted-foreground mt-1 font-sans">Where should we collect the bags?</p>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-sans mt-6">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Booking Pickup...
          </> : 'Schedule a Free Pickup'}
      </Button>
    </Form>;
}